import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
  ListItemText,
  Menu,
  ListItemIcon,
} from "@mui/material";
import { sortBy } from "underscore";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EventListItem from "./EventListItem";
import ImportEventModal from "./ImportEventModal";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  adminEventsSelector,
  buildNewEvent,
  fetchEvents,
} from "../../../redux/features/adminEventsSlice";
import {
  fetchEventsHistory,
  organizationsSliceSelector,
} from "../../../redux/features/organizationsSlice";
import {
  fetchRooms,
  fetchShows,
  showsAndRoomsSliceSelector,
} from "../../../redux/features/showsAndRoomsSlice";

export const sortedEvents = (events, filter) => {
  switch (filter) {
    case "upcoming":
      return sortBy(events, (event) => {
        return event?.startsAt?.seconds;
      });
    case "draft":
    case "past":
    case "all":
    default:
      return sortBy(events, (event) => {
        return -event?.startsAt?.seconds;
      });
  }
};

const EventsList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const eventsState = useSelector((state) => adminEventsSelector(state));
  const organizationsState = useSelector((state) =>
    organizationsSliceSelector(state),
  );
  const showsAndRoomsState = useSelector((state) =>
    showsAndRoomsSliceSelector(state),
  );
  const {
    rooms,
    shows,
    showsFetched,
    roomsFetched,
    showsLoading,
    roomsLoading,
  } = showsAndRoomsState;
  const [eventFilter, setEventFilter] = useState("upcoming");

  const { events, eventsLoading, eventsFetched } = eventsState;
  const { currentOrganization } = organizationsState;
  const { id: organizationId, eventsHistory } = currentOrganization || {};

  const [showImportEventModal, setShowImportEventModal] = useState(false);
  const [newEventAnchor, setNewEventAnchor] = useState(null);
  const newEventMenuOpen = Boolean(newEventAnchor);

  useEffect(() => {
    if (!eventsLoading && !eventsFetched) {
      dispatch(fetchEvents({ filter: eventFilter }));
    }
  }, [eventsLoading, eventsFetched, dispatch, eventFilter]);

  useEffect(() => {
    if (currentOrganization && !roomsLoading && !roomsFetched) {
      dispatch(fetchRooms({ organizationId }));
    }
  }, [
    roomsLoading,
    roomsFetched,
    currentOrganization,
    dispatch,
    organizationId,
  ]);

  useEffect(() => {
    if (currentOrganization && !showsLoading && !showsFetched) {
      dispatch(fetchShows({ organizationId }));
    }
  }, [
    showsLoading,
    showsFetched,
    currentOrganization,
    dispatch,
    organizationId,
  ]);

  const roomsList = Object.keys(rooms || {}).map((roomId) => rooms[roomId]);
  const showsList = Object.keys(shows || {}).map((showId) => shows[showId]);

  if (!eventsFetched) {
    return;
  }

  const closeNewEventMenu = () => {
    setNewEventAnchor(null);
  };
  const handleEventClick = (eventId) => {
    navigate(`/events/${eventId}`);
  };

  const handleImportClick = () => {
    closeNewEventMenu();
    dispatch(
      fetchEventsHistory({
        organizationId: organizationId,
      }),
    );
    setShowImportEventModal(true);
  };

  const handleCreateClick = async () => {
    closeNewEventMenu();
    await dispatch(buildNewEvent());
    navigate("/events/new");
  };

  return (
    <Box paddingBottom={"20px"}>
      <ImportEventModal
        open={showImportEventModal}
        eventsHistory={eventsHistory}
        handleClose={async () => await setShowImportEventModal(false)}
        shows={showsList}
        rooms={roomsList}
      />
      <Typography variant="h2" paddingTop={"20px"}>
        Events
      </Typography>
      <Box
        width={"100%"}
        sx={{
          paddingTop: "40px",
          paddingBottom: "40px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Box display={"flex"} gap={"20px"}>
          <TextField label="Search" type="search" />
          <FormControl sx={{ width: "200px" }}>
            <Select
              id="filter"
              name="filter"
              value={eventFilter}
              onChange={async (e) => {
                await setEventFilter(e.target.value);
                dispatch(fetchEvents({ filter: e.target.value }));
              }}
            >
              <MenuItem key="upcoming" value="upcoming">
                Upcoming Events
              </MenuItem>
              <MenuItem key="draft" value="draft">
                Draft Events
              </MenuItem>
              <MenuItem key="past" value="past">
                Past Events
              </MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Button
          id="basic-button"
          aria-controls={newEventMenuOpen ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={newEventMenuOpen ? "true" : undefined}
          onClick={(e) => {
            setNewEventAnchor(e.currentTarget);
          }}
        >
          Create Event
        </Button>
        <Menu
          id="newEventMenu"
          anchorEl={newEventAnchor}
          open={newEventMenuOpen}
          onClose={closeNewEventMenu}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={handleCreateClick}>
            <ListItemIcon>
              <NoteAddIcon fontSize="small" />
            </ListItemIcon>
            <Box>
              <ListItemText>Blank</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Create a new event from scratch
              </Typography>
            </Box>
          </MenuItem>

          <MenuItem onClick={handleImportClick}>
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <Box>
              <ListItemText>Copy</ListItemText>
              <Typography variant="body2" color="text.secondary">
                Create a new event by copying an existing event
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Box>
      {eventsLoading && (
        <LinearProgress position={"absolute"} sx={{ bottom: "-12px" }} />
      )}
      {!eventsLoading && events.length > 0 && (
        <Box sx={{ marginTop: "20px" }}>
          <Box display={"flex"} flexDirection={"column"} gap={"20px"}>
            {sortedEvents(events, eventFilter).map((event) => {
              return (
                <Box padding={0} key={`event-${event.id}`}>
                  <EventListItem event={event} handleClick={handleEventClick} />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {!eventsLoading && eventsFetched && events.length === 0 && (
        <Box
          display={"flex"}
          alignItems={"center"}
          justifyContent={"center"}
          paddingTop={"30px"}
        >
          <EventBusyIcon
            color="disabled"
            sx={{ height: "60px", width: "60px", marginRight: "60px" }}
          />
          <Typography color="text.disabled" variant="h2">
            No Events to show
          </Typography>
        </Box>
      )}
    </Box>
  );
};
export default EventsList;
